import type { FormEvent } from 'react'
import { useState } from 'react'

import { Check, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import type {
  SiteLocationDto,
  SiteLocationStatusDto,
} from '@/services/siteLocationService'
import {
  findPlannedStatus,
  normalizeLocationName,
} from '@/services/siteLocationService'

import { locationTypeCopy } from './locationTypeCopy'

import './SiteLocationFormDialog.css'

/**
 * One dialog for every location type, for both Add and the per-row edit icon
 * — the fields are the same, only the wording and the status choices (this
 * type's statuses) differ.
 */

export type SiteLocationFormValues = {
  name: string
  statusId: number
}

type Props = {
  typeName: string
  /** This type's statuses — the choices for the Status select. */
  statuses: SiteLocationStatusDto[]
  /** Omitted for Add; present for Edit. */
  location?: SiteLocationDto
  /** Every location in the project, across types: names are unique across all
      of them. Quick feedback only — the API has the final say. */
  allLocations: SiteLocationDto[]
  /** Resolves once saved; the dialog closes. A rejection keeps it open. */
  onSave: (values: SiteLocationFormValues) => Promise<void>
  onClose: () => void
}

const labelClassName =
  'grid gap-1.5 text-xs font-bold tracking-wide text-(--fg3) uppercase'

const selectClassName =
  'h-10 w-full rounded-md border border-(--border-strong) bg-(--white) px-2.5 text-sm text-(--fg1)'

function SiteLocationFormDialog({
  typeName,
  statuses,
  location,
  allLocations,
  onSave,
  onClose,
}: Props) {
  const isEdit = Boolean(location)
  const copy = locationTypeCopy(typeName)

  const [name, setName] = useState(location?.name ?? '')
  // A new location starts in its type's Planned status, picked by kind.
  const [statusId, setStatusId] = useState(
    location?.statusId ?? findPlannedStatus(statuses)?.id ?? 0,
  )
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const normalized = normalizeLocationName(name)
  const duplicate = allLocations.some(
    (other) =>
      other.id !== location?.id &&
      normalizeLocationName(other.name) === normalized,
  )
  const valid = normalized.length > 0 && !duplicate && statusId !== 0

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!valid || saving) return

    setSaving(true)
    setFailed(false)
    onSave({ name: name.trim(), statusId })
      .then(onClose)
      .catch(() => {
        setFailed(true)
        setSaving(false)
      })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false} className="gap-0 p-0 sm:max-w-lg">
        <form onSubmit={handleSubmit} className="grid">
          <DialogHeader className="relative gap-0.5 border-b border-(--border) px-6 py-5 pr-16">
            <p className="text-sm font-bold text-(--fg3)">
              {isEdit ? copy.editTitle : copy.newTitle}
            </p>
            <DialogTitle className="site-location-form__title">
              {name.trim() || copy.untitled}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isEdit
                ? `Update this ${typeName.toLowerCase()}.`
                : `Add a ${typeName.toLowerCase()} for this project.`}
            </DialogDescription>
            <DialogClose asChild>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-4 right-4"
              >
                <X aria-hidden="true" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>
          </DialogHeader>

          <div className="grid gap-4 px-6 py-5">
            <label htmlFor="site-location-name" className={labelClassName}>
              {copy.nameLabel}
              <Input
                id="site-location-name"
                className="h-10"
                value={name}
                placeholder={copy.namePlaceholder}
                aria-invalid={duplicate}
                aria-describedby={duplicate ? 'site-location-name-error' : undefined}
                onChange={(event) => setName(event.target.value)}
                required
              />
              {duplicate ? (
                <span
                  id="site-location-name-error"
                  role="alert"
                  className="text-xs font-normal tracking-normal text-destructive normal-case"
                >
                  A location with this name already exists in this project.
                </span>
              ) : null}
            </label>

            <label htmlFor="site-location-status" className={labelClassName}>
              Status
              <select
                id="site-location-status"
                className={selectClassName}
                value={statusId}
                onChange={(event) => setStatusId(Number(event.target.value))}
              >
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </label>

            {failed ? (
              <p role="alert" className="text-sm text-destructive">
                Could not save. Try again shortly.
              </p>
            ) : null}
          </div>

          <DialogFooter className="m-0 rounded-b-xl border-t border-(--border) px-6 py-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || saving}>
              <Check aria-hidden="true" />
              {isEdit ? 'Save changes' : copy.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SiteLocationFormDialog
